import type { ComponentChildren } from "preact";
import { useId } from "preact/hooks";
import { cx } from "./lib/cx.ts";

export interface ChoiceGroupOption<K extends string> {
  value: K;
  label: string;
  /** A quieter line under the label, such as what the option means. */
  detail?: string;
  /** A short mark beside the label, such as a StatusBadge reading "Suggested". It marks the option; it never chooses it. */
  badge?: ComponentChildren;
}

/**
 * Choose one, or several, of a few labelled options in a form that's sent
 * later. Rule: a fieldset whose legend asks the question, with native radio
 * buttons (`multiple` off) or checkboxes (`multiple` on), so arrow keys,
 * Space and screen readers work as they do everywhere; the whole row is the
 * label, so it's the target. Nothing is chosen until the person chooses:
 * `value` starts empty. For a setting that applies at once use a Switch; to
 * re-scope a list use a PillGroup.
 */
export function ChoiceGroup<K extends string>({ legend, hint, error, options, value, multiple = false, disabled, onChange, class: className }: {
  legend: string;
  hint?: string | undefined;
  /** Why the current choice can't be sent, shown under the options. */
  error?: string | undefined;
  options: ReadonlyArray<ChoiceGroupOption<K>>;
  /** The chosen options' values. */
  value: ReadonlyArray<K>;
  multiple?: boolean;
  disabled?: boolean;
  onChange: (value: K[]) => void;
  class?: string;
}) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  const toggle = (k: K, on: boolean) => {
    if (!multiple) return onChange(on ? [k] : []);
    // Kept in the options' order, whichever was chosen first.
    return onChange(options.map((o) => o.value).filter((v) => (v === k ? on : value.includes(v))));
  };
  return (
    <fieldset class={cx("ui-choices", className)} aria-describedby={describedBy} aria-invalid={error ? true : undefined} disabled={disabled}>
      <legend class="ui-choices__legend">{legend}</legend>
      {hint && <p id={`${id}-hint`} class="ui-field__hint">{hint}</p>}
      {options.map((o) => (
        <label key={o.value} class="ui-choices__option">
          <input type={multiple ? "checkbox" : "radio"} name={id} value={o.value} checked={value.includes(o.value)}
            onChange={(e) => toggle(o.value, e.currentTarget.checked)} />
          <span class="ui-choices__text">
            <span class="ui-choices__label">{o.label}{o.badge}</span>
            {o.detail && <span class="ui-choices__detail">{o.detail}</span>}
          </span>
        </label>
      ))}
      {error && <p id={`${id}-error`} class="ui-field__error">{error}</p>}
    </fieldset>
  );
}
